import React from "react";
import { formatRangeVal } from "../betTableLucky7/lucky7Utils";
import { aaaMarkets } from "./aaaUtils";
import { formatBook } from "../../../utils/formatOdds";

/**
 * Amar Akbar Anthony bet table (aaa / aaa2) in the reference admin's markup — rendered inside
 * the page's `.casino-detail` (reference IS_TABLET template):
 *   .row.row5.d-none-small (A. Amar / B. Akbar / C. Anthony back-lay + range)
 *   .row.row5.aaa-oe (Even/Odd, Black/Red, Under 7/Over 7 with their ranges)
 *   .mt-2 (13 single cards + range)
 * Styled by the ported reference rules in styles/index.css.
 *
 * Markets like the reference: `main` = subtype "aaa" (by sr), `fancy` = subtype "aaafancy" (by sr):
 *   fancy 0 Even, 1 Odd, 2 Red, 3 Black, 4-16 Card A..K, 17 Under 7, 18 Over 7.
 */
const IMG = `${import.meta.env.BASE_URL}img/cards`;
const SUIT = `${import.meta.env.BASE_URL}assets/img/icons`;
const CARD_VALUES = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
const PLAYERS = ["A. Amar", "B. Akbar", "C. Anthony"];

const isSuspended = (b) => String(b?.gstatus || "").toUpperCase() !== "OPEN";
const val = (v) => (v !== undefined && v !== null && v !== "" ? v : 0);

export default function BetTableA2({ sub = [], onBetClick, exposures = {} }) {
  const { main, fancy } = aaaMarkets(sub);
  const mk = (arr, i) => arr[i] || {};
  const getExposure = (name) => {
    const v = exposures[name] ?? exposures[name?.toLowerCase?.()];
    return v != null ? Number(v) || 0 : 0;
  };
  // Reference v-casinobook / v-casinofancybook: amount > 0 → book-green, otherwise book-red
  const bookCls = (b) => (getExposure(b?.nat) > 0 ? "book-green" : "book-red");
  const click = (b, betType, rate) => {
    if (isSuspended(b)) return;
    onBetClick?.(val(rate), b?.nat, betType, b);
  };
  const susp = (b) => (isSuspended(b) ? " suspended" : "");

  const player = (i) => {
    const b = mk(main, i);
    return (
      <div className="col-4">
        <div className="casino-box-row">
          <div className="casino-nation-name">
            <b>{PLAYERS[i]}</b>
          </div>{" "}
          <div className="casino-bl-box">
            <div className={`back casino-bl-box-item${susp(b)}`} onClick={() => click(b, "back", b?.b)}>
              <span className="casino-box-odd">{val(b?.b)}</span>
            </div>{" "}
            <div className={`lay casino-bl-box-item${susp(b)}`} onClick={() => click(b, "lay", b?.l)}>
              <span className="casino-box-odd">{val(b?.l)}</span>
            </div>
          </div>{" "}
          <div className={`casino-nation-name casino-book ${bookCls(b)}`}>{formatBook(getExposure(b?.nat))}</div>
        </div>
      </div>
    );
  };

  const range = (b, extra = " w-100", sep = "-") => (
    <div className={`teen1daycasino-container justify-content-end casino-min-max${extra}`}>
      <div>
        R:<span>{formatRangeVal(b?.min)}</span>
        {sep}
        <span>{formatRangeVal(b?.max)}</span>
      </div>
    </div>
  );

  // One .col-lg-4 group: odds row + two back boxes + range (reference: `oddsOf` shows odds, `statusOf` locks)
  const group = (left, right, rangeOf) => (
    <div className="col-lg-4 col-12">
      <div className="casino-box-row">
        <div className="casino-bl-box">
          <b>{val(left.odds?.b)}</b>
        </div>{" "}
        <div className="casino-bl-box">
          <b>{val(right.odds?.b)}</b>
        </div>
      </div>{" "}
      <div className="casino-box-row">
        {[left, right].map((side, i) => (
          <React.Fragment key={i}>
            {i > 0 && " "}
            <div className="casino-bl-box">
              <div
                className={`back casino-bl-box-item${side.img ? " casino-card-img" : ""}${susp(side.item)}`}
                onClick={() => click(side.item, "back", side.item?.b)}
              >
                {side.content}
              </div>{" "}
              <div className={`casino-book text-center ${bookCls(side.item)}`}>{formatBook(getExposure(side.item?.nat))}</div>
            </div>
          </React.Fragment>
        ))}
      </div>{" "}
      {range(rangeOf)}
    </div>
  );

  const f = (i) => mk(fancy, i);
  const odd = (label) => <span className="casino-box-odd">{label}</span>;
  const suits = (a, b) => (
    <span>
      <img src={`${SUIT}/${a}.png`} alt="" /> <img src={`${SUIT}/${b}.png`} alt="" />
    </span>
  );

  return (
    <>
      <div className="row row5 d-none-small">
        {player(0)} {player(1)} {player(2)}{" "}
        <div className="col-12">{range(mk(main, 0), "", " - ")}</div>
      </div>{" "}
      <div className="row row5 aaa-oe">
        {group(
          { odds: f(0), item: f(0), content: odd("Even") },
          { odds: f(1), item: f(1), content: odd("Odd") },
          f(0)
        )}{" "}
        {/* Reference quirk: the odds row shows fancy[2] (Red) then fancy[3] (Black) above Black | Red */}
        {group(
          { odds: f(2), item: f(3), content: suits("spade", "club"), img: true },
          { odds: f(3), item: f(2), content: suits("heart", "diamond"), img: true },
          f(2)
        )}{" "}
        {group(
          { odds: f(17), item: f(17), content: odd("Under 7") },
          { odds: f(18), item: f(18), content: odd("Over 7") },
          f(17)
        )}
      </div>{" "}
      <div className="mt-2">
        <div className="text-center w-100">
          <div className="casino-bl-box">
            <div className="casino-bl-box-item">
              <b>{val(f(4)?.b)}</b>
            </div>
          </div>
        </div>{" "}
        <div className="casino-cards text-center mt-1">
          {CARD_VALUES.map((v, i) => {
            const b = f(4 + i);
            return (
              <div key={v} className="casino-card-item">
                <div className={`card-image${susp(b)}`} onClick={() => click(b, "back", b?.b)}>
                  <img src={`${IMG}/${v}.png`} alt="" />
                </div>{" "}
                <div className={`casino-book text-center ${bookCls(b)}`}>{formatBook(getExposure(b?.nat))}</div>
              </div>
            );
          })}
        </div>{" "}
        {range(f(4))}
      </div>
    </>
  );
}
